function capitalize(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/** Matches Booking::CLIENT_EMAIL_FORMAT on the API. */
const CLIENT_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CET_TIMEZONE = "Europe/Sarajevo";

/**
 * Get "now" as a Date, but with components resolved in CET.
 * Useful for computing "today" or "tomorrow" anchored to Sarajevo, not the visitor's browser.
 */
function nowInCET() {
  const now = new Date();
  const cetStr = now.toLocaleString("en-US", { timeZone: CET_TIMEZONE });
  return new Date(cetStr);
}

/**
 * Extract the CET calendar date as YYYY-MM-DD from a Date object.
 * Uses Intl to resolve the date parts in Europe/Sarajevo regardless of browser timezone.
 */
function getDateInServerExpectedFormat(date) {
  const d = date instanceof Date ? date : new Date(date);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: CET_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
  return parts;
}

function formatDate(dateTimeString) {
  // Extract the date part from the ISO 8601 datetime string
  const datePart = dateTimeString.split("T")[0];

  return datePart;
}

function formatDateToString(date) {
  // Ensure the input is a valid Date object
  if (!(date instanceof Date)) {
    throw new Error("Invalid Date object");
  }
  // Array of short month names
  const shortMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  // Get the year, month, and day from the date
  const year = date.getFullYear();
  //const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const month = shortMonths[date.getMonth()];
  const day = String(date.getDate()).padStart(2, '0');

  // Format as yyyy-mm-dd
  return `${year}-${month}-${day}`;
}

const dateAfter = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const cetStr = d.toLocaleString("en-US", { timeZone: CET_TIMEZONE });
  let tomorrow = new Date(cetStr);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow;
};

function preparePhotosForUpload(photos) {
  const formData = new FormData();
  photos.forEach((file) => {
    formData.append("images[]", file);
  });
  return formData;
}

const UUID_FILENAME_PREFIX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i;

const googleDriveFileIdFromUrl = (url) => {
  if (!url) return null;
  const lh3Match = url.match(/googleusercontent\.com\/d\/([^=/?]+)/);
  if (lh3Match) return lh3Match[1];
  const driveMatch = url.match(/[?&]id=([^&]+)/);
  return driveMatch?.[1] ?? null;
};

const looksLikeStoredFileId = (name) =>
  Boolean(name) && !name.includes(".") && /^[a-zA-Z0-9_-]{15,}$/.test(name);

const stripUuidFromFilename = (name) =>
  name?.replace(UUID_FILENAME_PREFIX, "") || null;

const resolveBookingDocumentFileType = (image) => {
  if (image.fileType) return String(image.fileType).toLowerCase();
  const name = image.originalFilename || image.name || "";
  const ext = name.split(".").pop()?.toLowerCase();
  return ext === "pdf" ? "pdf" : ext || "image";
};

const resolveBookingDocumentPreviewUrl = (image) => {
  if (resolveBookingDocumentFileType(image) === "pdf") return null;

  const driveId =
    image.googleDriveImageId || googleDriveFileIdFromUrl(image.url);
  if (driveId) {
    return `https://drive.google.com/thumbnail?id=${driveId}&sz=w200`;
  }

  return image.thumbnailUrl || image.url;
};

const resolvePersistedDocumentName = (image) => {
  if (image.originalFilename) return image.originalFilename;

  const strippedName = stripUuidFromFilename(image.name);
  if (strippedName && !looksLikeStoredFileId(strippedName)) return strippedName;

  if (image.name && !looksLikeStoredFileId(image.name)) return image.name;

  return "document";
};

const mapPersistedBookingDocument = (image) => {
  const name = resolvePersistedDocumentName(image);
  const fileType = resolveBookingDocumentFileType({ ...image, name });
  const previewUrl = resolveBookingDocumentPreviewUrl({ ...image, name });

  return {
    id: image.id,
    url: previewUrl || image.url,
    previewUrl,
    name,
    fileType,
  };
};

const truncateName = (name, maxLength = 13) => {
  if (name.length > maxLength) {
    return name.slice(0, maxLength) + "...";
  }
  return name;
};

// Function to parse the double value
const parseCurrencyToDouble = (currencyStr) => {
  // Remove non-numeric characters (except for the decimal point)
  const numericString = currencyStr.replace(/[^\d.-]+/g, "");
  return parseFloat(numericString);
};

/**
 * Format a datetime string/Date as a full date+time in CET, with "(CET)" suffix.
 * Example: "Apr 12, 2026, 14:30 (CET)"
 */
function formatDateTimeCET(value) {
  const date = value instanceof Date ? value : new Date(value);
  const formatted = date.toLocaleString("en-GB", {
    timeZone: CET_TIMEZONE,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${formatted} (CET)`;
}

/**
 * Format a datetime string/Date as a date-only string in CET.
 * Example: "Monday, April 12, 2026"
 */
function formatDateLongCET(value, options = {}) {
  const date = value instanceof Date ? value : new Date(value);
  const defaults = { timeZone: CET_TIMEZONE, weekday: "long", year: "numeric", month: "long", day: "numeric" };
  return date.toLocaleDateString("en-GB", { ...defaults, ...options });
}

/**
 * Format a datetime string/Date as a short date in CET.
 * Example: "12 Apr 2026"
 */
function formatDateShortCET(value) {
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleDateString("en-GB", {
    timeZone: CET_TIMEZONE,
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Append "(CET)" to a wall-clock time string like "08:30".
 */
function labelTimeCET(timeStr) {
  return `${timeStr} (CET)`;
}

/**
 * Return a Date whose Y/M/D reflect "today" in CET.
 * Unlike nowInCET() which includes hours/minutes, this is midnight-anchored
 * and safe for use as calendar minDate in date pickers.
 */
function todayDateInCET() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: CET_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  const [y, m, d] = parts.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Add `days` to a Date, keeping arithmetic anchored to CET calendar.
 * Returns midnight on the target day so date pickers compare correctly.
 */
function dateAddDaysCET(date, days) {
  const d = date instanceof Date ? date : new Date(date);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: CET_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
  const [y, m, day] = parts.split("-").map(Number);
  return new Date(y, m - 1, day + days);
}

/**
 * Add `months` to a Date, keeping arithmetic anchored to CET calendar.
 */
function dateAddMonthsCET(date, months) {
  const d = date instanceof Date ? date : new Date(date);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: CET_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
  const [y, m, day] = parts.split("-").map(Number);
  return new Date(y, m - 1 + months, day);
}

/** Minimum passport expiry: at least 3 months after trip checkout / drop-off. */
function minimumPassportExpiryDateCET(checkoutOrDropOffDate) {
  if (!checkoutOrDropOffDate) return null;
  return dateAddMonthsCET(checkoutOrDropOffDate, 3);
}

function isCalendarDayBefore(date, minimumDate) {
  const normalize = (value) => {
    const d = value instanceof Date ? value : new Date(value);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  return normalize(date).getTime() < normalize(minimumDate).getTime();
}

/** Extract a human-readable message from an axios/API error response. */
function getApiErrorMessage(error, fallback = "Something went wrong") {
  const data = error?.response?.data;
  if (typeof data?.error === "string") return data.error;
  if (typeof data?.message === "string") return data.message;
  if (Array.isArray(data?.errors) && data.errors[0]) return String(data.errors[0]);
  return error?.message || fallback;
}

/** Passport + plane ticket images required before checkout / affiliate price step. */
function bookingHasRequiredDocuments(booking) {
  if (!booking) return false;
  const images = booking.images ?? [];
  const hasPassport = images.some((image) => image.reservationPassport);
  const hasTicket = images.some((image) => !image.reservationPassport);
  return hasPassport && hasTicket;
}

export {
  capitalize,
  CLIENT_EMAIL_REGEX,
  getApiErrorMessage,
  getDateInServerExpectedFormat,
  formatDate,
  formatDateToString,
  preparePhotosForUpload,
  dateAfter,
  truncateName,
  mapPersistedBookingDocument,
  resolveBookingDocumentPreviewUrl,
  parseCurrencyToDouble,
  bookingHasRequiredDocuments,
  CET_TIMEZONE,
  nowInCET,
  formatDateTimeCET,
  formatDateLongCET,
  formatDateShortCET,
  labelTimeCET,
  todayDateInCET,
  dateAddDaysCET,
  dateAddMonthsCET,
  minimumPassportExpiryDateCET,
  isCalendarDayBefore,
};
